import { ApiError } from '@/lib/api';
import type {
  ReplaySettingsChange,
  ReplaySettingsInput,
  ReplayTextKey,
  ReplayTexts,
} from '@matter_extractor/shared';
import {
  REPLAY_MODE_TEXT_MAX,
  REPLAY_RULES_TEXT_MAX,
  REPLAY_SEED_AUTHOR,
} from '@matter_extractor/shared';

/**
 * The words and the rules of Réglages > Rejeu (docs/specs/rejeu.md §10.3, issue
 * #193), PURE.
 *
 * Everything the screen decides that is not a tag lives here, so that the
 * components stay drawing and the rules are measured: which texts moved, which
 * one the server would refuse and why, how a count of replays is said in
 * French, and how a line of the journal reads as a sentence.
 *
 * A refusal is worded from what the schema bounds, never from the server's
 * `message` (a developer's string): the same régime as `rules-labels.ts`.
 */

/* ------------------------------------------------------------- the three texts */

/** One of the three texts as the screen draws it. */
export interface ReplayTextField {
  readonly key: ReplayTextKey;
  readonly label: string;
  /** The line under the label: when this text is served. */
  readonly hint: string;
  readonly max: number;
  /** The height of the field, in rows: the common rules are six paragraphs. */
  readonly rows: number;
}

/**
 * The three fields, in the order the instruction is SERVED (§10.1): the common
 * rules, then the paragraph of the mode. The two modes are drawn side by side,
 * so the order is also the reading order of the planche.
 */
export const REPLAY_TEXT_FIELDS: readonly ReplayTextField[] = [
  {
    key: 'rules',
    label: 'Règles communes',
    hint: 'Servies à chaque rejeu, quel que soit le mode.',
    max: REPLAY_RULES_TEXT_MAX,
    rows: 16,
  },
  {
    key: 'strict',
    label: 'Mode strict',
    hint: 'Ajouté aux règles communes quand le rejeu démarre en mode strict.',
    max: REPLAY_MODE_TEXT_MAX,
    rows: 8,
  },
  {
    key: 'completed',
    label: 'Mode complété',
    hint: 'Ajouté aux règles communes quand le rejeu démarre en mode complété.',
    max: REPLAY_MODE_TEXT_MAX,
    rows: 8,
  },
];

/** The name of a text, as a sentence names it. */
export const REPLAY_TEXT_LABEL: Readonly<Record<ReplayTextKey, string>> = {
  rules: 'Règles communes',
  strict: 'Mode strict',
  completed: 'Mode complété',
};

/** The bound of a text, from the schema's own constants. */
export function replayTextMax(key: ReplayTextKey): number {
  return key === 'rules' ? REPLAY_RULES_TEXT_MAX : REPLAY_MODE_TEXT_MAX;
}

/** The three texts of anything that carries them, and nothing else it carries. */
export function textsOf(source: ReplayTexts): ReplayTexts {
  return { rules: source.rules, strict: source.strict, completed: source.completed };
}

/**
 * The texts the draft changed, in the order of the screen.
 *
 * Compared as typed: the server trims on save, so what it holds never has a
 * trailing space, and a draft that only added one is a move worth saving no
 * more than it is worth hiding - the box says « Mode strict » and the operator
 * sees what they did.
 */
export function touchedKeys(saved: ReplayTexts, draft: ReplayTexts): ReplayTextKey[] {
  return REPLAY_TEXT_FIELDS.filter((field) => saved[field.key] !== draft[field.key]).map(
    (field) => field.key,
  );
}

/* ------------------------------------------------------------ what is refused */

/** Why a text would be refused: nothing in it, or past its bound. */
export type ReplayTextProblem = 'empty' | 'too-long';

/**
 * What the server would refuse in a text, or null.
 *
 * The schema TRIMS before it measures (`z.string().trim().min(1).max(...)`), so
 * a text of spaces is empty and the bound counts the trimmed text. Checked here
 * first, in the page, so that the refusal is read under the field it is about
 * before a request is spent on it.
 */
export function textProblem(key: ReplayTextKey, value: string): ReplayTextProblem | null {
  const length = value.trim().length;
  if (length === 0) return 'empty';
  return length > replayTextMax(key) ? 'too-long' : null;
}

/** The refusals of a draft, by text. A text that holds is absent. */
export function textProblems(
  draft: ReplayTexts,
): Partial<Record<ReplayTextKey, ReplayTextProblem>> {
  const problems: Partial<Record<ReplayTextKey, ReplayTextProblem>> = {};
  for (const field of REPLAY_TEXT_FIELDS) {
    const problem = textProblem(field.key, draft[field.key]);
    if (problem !== null) problems[field.key] = problem;
  }
  return problems;
}

/** The refusal, said under the field it is about. */
export function problemSentence(key: ReplayTextKey, problem: ReplayTextProblem): string {
  return problem === 'empty'
    ? 'Ce texte ne peut pas être vide.'
    : `Ce texte dépasse la borne : ${replayTextMax(key)} caractères au plus.`;
}

/**
 * The body of the `PUT`: the three texts and the reason, which is NULL when the
 * field was left empty (the schema reads an empty string as null as well, but a
 * body that says what it means is one a reader of the network panel can trust).
 */
export function saveInputOf(draft: ReplayTexts, note: string): ReplaySettingsInput {
  const reason = note.trim();
  return { ...textsOf(draft), note: reason === '' ? null : reason };
}

/**
 * The texts a 400 of the server NAMES, in the order of the screen.
 *
 * The body of a refusal is the envelope of `errorHandler`: `details` is Zod's
 * `flatten()`, whose `fieldErrors` is keyed by the name of the field that failed
 * (`rules`, `strict`, `completed`). Read defensively, because `details` is
 * unparsed and a refusal that names no text - a body that carries a key the
 * schema does not know, an unreadable one - reads as an empty list and the
 * caller says the generic sentence.
 */
export function refusedTexts(error: unknown): ReplayTextKey[] {
  if (!(error instanceof ApiError) || error.status !== 400) return [];
  const details = error.details;
  if (typeof details !== 'object' || details === null || !('fieldErrors' in details)) return [];
  const fields = details.fieldErrors;
  if (typeof fields !== 'object' || fields === null) return [];
  return REPLAY_TEXT_FIELDS.filter((field) => field.key in fields).map((field) => field.key);
}

/** A failed save, in French: the three ways it fails that the operator can act on. */
export function saveFailureSentence(error: unknown): string {
  if (error instanceof ApiError && error.status === 400) {
    const named = refusedTexts(error);
    return named.length === 0
      ? 'Le serveur a refusé un des textes (vide ou trop long). Rien n’a été écrit.'
      : `Le serveur a refusé ${touchedSentence(named)} (vide ou trop long). Rien n’a été écrit.`;
  }
  if (error instanceof ApiError && error.status === 404) {
    return 'Le rejeu est fermé sur ce serveur : rien n’a été écrit.';
  }
  return 'L’enregistrement a échoué. Les textes de la base n’ont pas bougé.';
}

/* --------------------------------------------------------------- the sentences */

/**
 * « 0 rejeu déjà joué », « 1 rejeu déjà joué », « 3 rejeux déjà joués ».
 *
 * Written out rather than a plural `s` after a count: zero is singular in
 * French, which is what a ternary that only knew about « 1 » gets wrong - the
 * lesson `levelDeletionSentence` records.
 */
export function replayCountSentence(count: number): string {
  return count > 1 ? `${count} rejeux déjà joués` : `${count} rejeu déjà joué`;
}

/** « A », « A et B », « A, B et C » - the list of a sentence. */
export function joinFrench(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1] ?? ''}`;
}

/** « Règles communes et Mode complété », plain: the box lists what the operator touched. */
export function touchedSentence(keys: readonly ReplayTextKey[]): string {
  return joinFrench(keys.map((key) => REPLAY_TEXT_LABEL[key]));
}

/** « Règles communes » et « Mode complété », in French quotes: the journal names what was replaced. */
export function replacedSentence(keys: readonly ReplayTextKey[]): string {
  return joinFrench(keys.map((key) => `« ${REPLAY_TEXT_LABEL[key]} »`));
}

/** Who a line or a save is signed by: the seed has no operator. */
export function authorLabel(author: string): string {
  return author === REPLAY_SEED_AUTHOR ? 'textes de départ' : author;
}

/** The calendar day of a line, as the journal of Réglages > Modèles prints it. */
export function journalDay(change: Pick<ReplaySettingsChange, 'changedAt'>): string {
  return change.changedAt.slice(0, 10);
}

/**
 * The texts a line of the journal can OPEN, with what each held before and
 * after: the replaced ones, in the order of the screen.
 *
 * A seed line has no « avant » (`before` is null), and a line that somehow
 * carries none reads the same - the panel shows the text as it stands after.
 */
export interface ReplayJournalDiff {
  readonly key: ReplayTextKey;
  readonly before: string | null;
  readonly after: string;
}

export function journalDiffs(change: ReplaySettingsChange): ReplayJournalDiff[] {
  const replaced = new Set<ReplayTextKey>(change.replaced);
  return REPLAY_TEXT_FIELDS.filter((field) => replaced.has(field.key)).map((field) => ({
    key: field.key,
    before: change.before === null ? null : change.before[field.key],
    after: change.after[field.key],
  }));
}
